import {
  CustomFieldType,
  type CustomField,
  type CustomFieldDefinition,
  type Resource,
  type ResourceRate
} from '__types__';
import type { ResourcePatch } from 'v5/core/entity-values/resourcePatch.types';
import type { CustomFieldColumns } from 'v5/core/entity-values/values.types';
import type { ResourceWriteModel } from 'v5/core/resource-write/ResourceWriteModel';

/**
 * The seam between the form builder and the resource entities for money.
 * The entities (resource cache, v1 store, `ResourceMutationService`) store
 * amounts in hundredths: `rates[].amount`, `rates[].rate.amount`, rate cards,
 * and `integer_value` of CURRENCY custom fields. The builder (schema, codecs,
 * patches, body builders, fields) works in the amount itself (123.45).
 * Every resource the builder reads and every patch it writes passes here.
 */
const HUNDREDTHS = 100;

/** Definition ids of CURRENCY custom fields (top level and group members). */
export type CurrencyDefinitionIds = ReadonlySet<number>;

export function currencyDefinitionIds(
  definitions: Record<number, CustomFieldDefinition>
): CurrencyDefinitionIds {
  return new Set(
    Object.values(definitions)
      .filter(definition => definition.data_type === CustomFieldType.CURRENCY)
      .map(definition => definition.id)
  );
}

/** Stored hundredths → amount; `null` / `undefined` stay as they are. */
export function amountToBuilder<T extends number | null | undefined>(
  stored: T
): T {
  return (typeof stored === 'number' ? stored / HUNDREDTHS : stored) as T;
}

/** Amount → stored hundredths, rounded (19.99 → 1999). */
export function amountToEntity(amount: number | null): number | null {
  return amount === null ? null : Math.round(amount * HUNDREDTHS);
}

function rateToBuilder(rate: ResourceRate): ResourceRate {
  return {
    ...rate,
    amount: amountToBuilder(rate.amount),
    // `rate` is typed as always present but only joined for rate-card rows.
    ...(rate.rate
      ? { rate: { ...rate.rate, amount: amountToBuilder(rate.rate.amount) } }
      : {})
  };
}

function customFieldToBuilder(
  row: CustomField,
  currencyIds: CurrencyDefinitionIds
): CustomField {
  return currencyIds.has(row.custom_field_definition_id)
    ? { ...row, integer_value: amountToBuilder(row.integer_value) }
    : row;
}

/** A resource as the entities hold it → the resource the builder decodes. */
export function resourceToBuilder(
  resource: Resource,
  currencyIds: CurrencyDefinitionIds
): Resource {
  return {
    ...resource,
    rates: resource.rates?.map(rateToBuilder),
    custom_fields: resource.custom_fields?.map(row =>
      customFieldToBuilder(row, currencyIds)
    ),
    custom_fieldgroups: resource.custom_fieldgroups?.map(group => ({
      ...group,
      custom_fields: group.custom_fields?.map(row =>
        customFieldToBuilder(row, currencyIds)
      )
    }))
  };
}

function columnsToEntity(columns: CustomFieldColumns): CustomFieldColumns {
  return { ...columns, integer_value: amountToEntity(columns.integer_value) };
}

/** A patch the builder encoded → the patch the entities' write model takes. */
export function patchToEntity(
  patch: ResourcePatch,
  currencyIds: CurrencyDefinitionIds
): ResourcePatch {
  switch (patch.kind) {
    case 'core':
    case 'contact-details':
    case 'tags':
    case 'groups':
    case 'locations':
      return patch;
    case 'rates':
      return {
        ...patch,
        items: patch.items.map(item => ({
          ...item,
          amount: amountToEntity(item.amount)
        }))
      };
    case 'custom-field':
      if (patch.write.type === 'clear' || !currencyIds.has(patch.definitionId))
        return patch;
      return {
        ...patch,
        write: { type: 'set', columns: columnsToEntity(patch.write.columns) }
      };
    case 'custom-field-group':
      return {
        ...patch,
        rows: patch.rows.map(row => ({
          ...row,
          members: row.members.map(member =>
            currencyIds.has(member.definitionId)
              ? { ...member, columns: columnsToEntity(member.columns) }
              : member
          )
        }))
      };
  }
}

/**
 * The write model the builder calls: patches go out in entity units, the
 * resource `apply` returns comes back in builder units (the agent save
 * decodes it). `create` returns the entity as is: it goes to the host.
 */
export function builderWriteModel(
  entityWriteModel: ResourceWriteModel,
  currencyIds: CurrencyDefinitionIds
): ResourceWriteModel {
  return {
    apply: async (resourceId, patch) =>
      resourceToBuilder(
        await entityWriteModel.apply(
          resourceId,
          patchToEntity(patch, currencyIds)
        ),
        currencyIds
      ),
    create: input =>
      entityWriteModel.create({
        ...input,
        patches: input.patches.map(patch => patchToEntity(patch, currencyIds))
      })
  };
}
